/**
 * Schedule Parser
 *
 * Parses EnergyPlus schedule objects (Schedule:Compact, Schedule:Constant, etc.)
 * into structured format for the visual editor.
 */

import type { IDFObject } from '../parser';
import type {
  ParsedSchedule,
  SchedulePeriod,
  DaySchedule,
  TimeValue,
  DayType,
  ScheduleType,
  ScheduleDate,
  ScheduleTypeLimits,
} from './types';

/**
 * Generate a unique ID
 */
function generateId(): string {
  return `sch_${String(Date.now())}_${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Parse a date string like "12/31" or "12/31"
 */
function parseDate(dateStr: string): ScheduleDate {
  const cleaned = dateStr.trim();
  const parts = cleaned.split('/');
  if (parts.length !== 2) {
    throw new Error(`Invalid date format: ${dateStr}`);
  }
  return {
    month: parseInt(parts[0] || '1', 10),
    day: parseInt(parts[1] || '1', 10),
  };
}

/**
 * Parse a time string like "06:00" or "6:00"
 */
function parseTime(timeStr: string): { hour: number; minute: number } {
  const cleaned = timeStr.trim().replace(',', '');
  const parts = cleaned.split(':');
  if (parts.length !== 2) {
    throw new Error(`Invalid time format: ${timeStr}`);
  }
  return {
    hour: parseInt(parts[0] || '0', 10),
    minute: parseInt(parts[1] || '0', 10),
  };
}

/**
 * Parse day types from a string like "Weekdays" or "Monday Tuesday Wednesday"
 */
function parseDayTypes(dayTypesStr: string): DayType[] {
  const parts = dayTypesStr.trim().split(/\s+/);
  const result: DayType[] = [];

  for (const part of parts) {
    const cleaned = part.trim();
    if (!cleaned) continue;

    // Handle combined types
    if (cleaned.toLowerCase() === 'weekdays') {
      result.push('Weekdays');
    } else if (cleaned.toLowerCase() === 'weekends') {
      result.push('Weekends');
    } else if (cleaned.toLowerCase() === 'alldays') {
      result.push('AllDays');
    } else if (cleaned.toLowerCase() === 'allotherdays') {
      result.push('AllOtherDays');
    } else if (cleaned.toLowerCase() === 'holidays') {
      result.push('Holidays');
    } else if (cleaned.toLowerCase() === 'summerdesignday') {
      result.push('SummerDesignDay');
    } else if (cleaned.toLowerCase() === 'winterdesignday') {
      result.push('WinterDesignDay');
    } else if (cleaned.toLowerCase() === 'customday1') {
      result.push('CustomDay1');
    } else if (cleaned.toLowerCase() === 'customday2') {
      result.push('CustomDay2');
    } else {
      // Check for individual days
      const dayMap: Record<string, DayType> = {
        monday: 'Monday',
        tuesday: 'Tuesday',
        wednesday: 'Wednesday',
        thursday: 'Thursday',
        friday: 'Friday',
        saturday: 'Saturday',
        sunday: 'Sunday',
      };
      const day = dayMap[cleaned.toLowerCase()];
      if (day) {
        result.push(day);
      }
    }
  }

  return result;
}

/**
 * Calculate the next day after a given date
 */
function nextDay(date: ScheduleDate): ScheduleDate {
  const daysInMonth: Record<number, number> = {
    1: 31,
    2: 28, // Ignoring leap years for simplicity
    3: 31,
    4: 30,
    5: 31,
    6: 30,
    7: 31,
    8: 31,
    9: 30,
    10: 31,
    11: 30,
    12: 31,
  };

  const maxDays = daysInMonth[date.month] || 31;
  if (date.day < maxDays) {
    return { month: date.month, day: date.day + 1 };
  } else if (date.month < 12) {
    return { month: date.month + 1, day: 1 };
  } else {
    return { month: 1, day: 1 }; // Wrap around to next year
  }
}

/**
 * Parse a Schedule:Compact object into structured format
 */
export function parseScheduleCompact(obj: IDFObject): ParsedSchedule {
  const name = obj.fields[0]?.value?.toString() || '';
  const typeLimits = obj.fields[1]?.value?.toString() || null;
  const fields = obj.fields.slice(2); // Skip name and type limits

  const periods: SchedulePeriod[] = [];
  let currentPeriod: SchedulePeriod | null = null;
  let currentDaySchedule: DaySchedule | null = null;

  let i = 0;
  while (i < fields.length) {
    const field = fields[i];
    if (!field) {
      i++;
      continue;
    }

    const value = field.value.toString().trim();

    if (value.toLowerCase().startsWith('through:')) {
      // Start new period
      const dateStr = value.replace(/through:/i, '').trim().replace(',', '');
      const endDate = parseDate(dateStr);

      // Save current day schedule to current period
      if (currentDaySchedule && currentPeriod) {
        currentPeriod.daySchedules.push(currentDaySchedule);
      }

      // Save current period
      if (currentPeriod) {
        periods.push(currentPeriod);
      }

      // Create new period
      const lastPeriod = periods[periods.length - 1];
      currentPeriod = {
        id: generateId(),
        startDate: lastPeriod ? nextDay(lastPeriod.endDate) : { month: 1, day: 1 },
        endDate,
        daySchedules: [],
      };
      currentDaySchedule = null;
      i++;
    } else if (value.toLowerCase().startsWith('for:')) {
      // New day type specification
      if (currentDaySchedule && currentPeriod) {
        currentPeriod.daySchedules.push(currentDaySchedule);
      }

      const dayTypesStr = value.replace(/for:/i, '').trim().replace(',', '');
      currentDaySchedule = {
        dayTypes: parseDayTypes(dayTypesStr),
        values: [],
      };
      i++;
    } else if (value.toLowerCase().startsWith('until:')) {
      // Time-value pair
      const timeStr = value.replace(/until:/i, '').trim().replace(',', '');
      const { hour, minute } = parseTime(timeStr);

      // Get the value from the next field
      i++;
      const valueField = fields[i];
      const numValue = valueField ? parseFloat(valueField.value.toString()) : 0;

      if (currentDaySchedule) {
        currentDaySchedule.values.push({
          hour,
          minute,
          value: numValue,
        });
      }
      i++;
    } else if (value.toLowerCase().startsWith('interpolate:')) {
      // Skip interpolate directive
      i++;
    } else {
      // Unknown field, skip
      i++;
    }
  }

  // Push final day schedule and period
  if (currentDaySchedule && currentPeriod) {
    currentPeriod.daySchedules.push(currentDaySchedule);
  }
  if (currentPeriod) {
    periods.push(currentPeriod);
  }

  return {
    id: generateId(),
    name,
    typeLimits: typeLimits && typeLimits.trim() !== '' ? typeLimits : null,
    scheduleType: 'Schedule:Compact',
    periods,
    idfObjectId: obj.id,
  };
}

/**
 * Parse a Schedule:Constant object into structured format
 */
export function parseScheduleConstant(obj: IDFObject): ParsedSchedule {
  const name = obj.fields[0]?.value?.toString() || '';
  const typeLimits = obj.fields[1]?.value?.toString() || null;
  const hourlyValue = obj.fields[2] ? parseFloat(obj.fields[2].value.toString()) : 1;

  // Create a simple all-day schedule with constant value
  return {
    id: generateId(),
    name,
    typeLimits: typeLimits && typeLimits.trim() !== '' ? typeLimits : null,
    scheduleType: 'Schedule:Constant',
    periods: [
      {
        id: generateId(),
        startDate: { month: 1, day: 1 },
        endDate: { month: 12, day: 31 },
        daySchedules: [
          {
            dayTypes: ['AllDays'],
            values: [{ hour: 24, minute: 0, value: hourlyValue }],
          },
        ],
      },
    ],
    constantValue: hourlyValue,
    idfObjectId: obj.id,
  };
}

/**
 * Parse a Schedule:Day:Hourly object into structured format
 */
export function parseScheduleDayHourly(obj: IDFObject): ParsedSchedule {
  const name = obj.fields[0]?.value?.toString() || '';
  const typeLimits = obj.fields[1]?.value?.toString() || null;

  // Fields 2-25 are hourly values (24 values)
  const values: TimeValue[] = [];
  for (let hour = 1; hour <= 24; hour++) {
    const field = obj.fields[hour + 1]; // +1 because fields 0 and 1 are name and type limits
    const value = field ? parseFloat(field.value.toString()) : 0;
    values.push({ hour, minute: 0, value });
  }

  return {
    id: generateId(),
    name,
    typeLimits: typeLimits && typeLimits.trim() !== '' ? typeLimits : null,
    scheduleType: 'Schedule:Day:Hourly',
    periods: [
      {
        id: generateId(),
        startDate: { month: 1, day: 1 },
        endDate: { month: 12, day: 31 },
        daySchedules: [
          {
            dayTypes: ['AllDays'],
            values,
          },
        ],
      },
    ],
    idfObjectId: obj.id,
  };
}

/**
 * Parse any supported schedule type
 */
export function parseSchedule(obj: IDFObject): ParsedSchedule | null {
  const scheduleType = obj.className as ScheduleType;

  switch (scheduleType) {
    case 'Schedule:Compact':
      return parseScheduleCompact(obj);
    case 'Schedule:Constant':
      return parseScheduleConstant(obj);
    case 'Schedule:Day:Hourly':
      return parseScheduleDayHourly(obj);
    // Add more schedule types as needed
    default:
      // For unsupported types, create a minimal parsed schedule
      if (obj.className.startsWith('Schedule:')) {
        return {
          id: generateId(),
          name: obj.fields[0]?.value?.toString() || '',
          typeLimits: obj.fields[1]?.value?.toString() || null,
          scheduleType: scheduleType,
          periods: [],
          idfObjectId: obj.id,
        };
      }
      return null;
  }
}

/**
 * Parse ScheduleTypeLimits object
 */
export function parseScheduleTypeLimits(obj: IDFObject): ScheduleTypeLimits | null {
  if (obj.className !== 'ScheduleTypeLimits') {
    return null;
  }

  const name = obj.fields[0]?.value?.toString() || '';
  const lowerStr = obj.fields[1]?.value?.toString();
  const upperStr = obj.fields[2]?.value?.toString();
  const numericType = obj.fields[3]?.value?.toString() as 'Continuous' | 'Discrete' | undefined;
  const unitType = obj.fields[4]?.value?.toString();

  const result: ScheduleTypeLimits = { name };

  if (lowerStr && lowerStr.trim() !== '') {
    result.lowerLimit = parseFloat(lowerStr);
  }
  if (upperStr && upperStr.trim() !== '') {
    result.upperLimit = parseFloat(upperStr);
  }
  if (numericType === 'Continuous' || numericType === 'Discrete') {
    result.numericType = numericType;
  }
  if (unitType && unitType.trim() !== '') {
    result.unitType = unitType;
  }

  return result;
}

/**
 * Check if an IDF object class is a schedule type
 */
export function isScheduleClass(className: string): boolean {
  return (
    className === 'Schedule:Compact' ||
    className === 'Schedule:Constant' ||
    className === 'Schedule:Day:Hourly' ||
    className === 'Schedule:Day:Interval' ||
    className === 'Schedule:Day:List' ||
    className === 'Schedule:Week:Daily' ||
    className === 'Schedule:Week:Compact' ||
    className === 'Schedule:Year' ||
    className === 'Schedule:File'
  );
}

/**
 * Get all schedule objects from an IDF document
 */
export function getScheduleObjects(objects: IDFObject[]): IDFObject[] {
  return objects.filter((obj) => isScheduleClass(obj.className));
}

/**
 * Get all ScheduleTypeLimits objects from an IDF document
 */
export function getScheduleTypeLimitsObjects(objects: IDFObject[]): IDFObject[] {
  return objects.filter((obj) => obj.className === 'ScheduleTypeLimits');
}
